const screenPermission = (screenCode, overrides = {}) => ({
  screenCode,
  screenName: screenCode,
  canView: true,
  canCreate: true,
  canUpdate: true,
  canDelete: false,
  ...overrides,
});

const fullAccess = (screenCode, canDelete = false) =>
  screenPermission(screenCode, { canDelete });

const mapScreenPermissionsFromApi = (screenPermissions) =>
  (Array.isArray(screenPermissions) ? screenPermissions : [])
    .filter((item) => item?.screenCode)
    .map((item) => ({
      screenCode: String(item.screenCode).toUpperCase(),
      screenName: item.screenName || item.screenCode,
      canView: Boolean(item.canView),
      canCreate: Boolean(item.canCreate),
      canUpdate: Boolean(item.canUpdate),
      canDelete: Boolean(item.canDelete),
    }));

const deriveFromAuthorities = (role, authorities) => {
  const isCallAdmin = role === "CALL_ADMIN";
  const permissions = [];

  if (authorities.includes("ROLE_CC_INBOX")) {
    permissions.push(fullAccess("CC_INBOX"));
  }
  if (authorities.includes("ROLE_CC_CAMPAIGNS")) {
    permissions.push(fullAccess("CC_DASHBOARD"));
    permissions.push(fullAccess("CC_WEEKLY_TRACKER"));
    permissions.push(fullAccess("CC_CAMPAIGNS"));
    permissions.push(
      screenPermission("CC_AGENT_ALERTS", {
        canCreate: false,
        canUpdate: true,
        canDelete: false,
      })
    );
  }
  if (authorities.includes("ROLE_CC_AGENT_WORKSPACE")) {
    permissions.push(fullAccess("CC_TICKETS", true));
    permissions.push(fullAccess("CC_AGENT_WORKSPACE", true));
    permissions.push(fullAccess("CC_TICKET_COLUMNS", true));
    permissions.push(fullAccess("CC_PAYMENT_REMINDERS"));
    permissions.push(fullAccess("CC_PAYMENT_CONFIRMATIONS"));
    permissions.push(
      screenPermission("CC_ACCOUNT_PLAN", {
        canCreate: false,
        canUpdate: false,
        canDelete: false,
      })
    );
    if (!authorities.includes("ROLE_CC_CAMPAIGNS")) {
      permissions.push(
        screenPermission("CC_AGENT_ALERTS", {
          canCreate: false,
          canUpdate: false,
          canDelete: false,
        })
      );
    }
    if (!authorities.includes("ROLE_CC_CAMPAIGNS")) {
      permissions.push(
        screenPermission("CC_DASHBOARD", {
          canCreate: false,
          canUpdate: false,
          canDelete: false,
        })
      );
    }
  }
  if (authorities.includes("ROLE_CC_SETTINGS")) {
    permissions.push(fullAccess("CC_SETTINGS", true));
    permissions.push(fullAccess("CC_PAYMENT_PROOF_TEMPLATE"));
    permissions.push(fullAccess("CC_WHATSAPP_SETTINGS"));
    permissions.push(fullAccess("CC_PAYMENT_CONFIRMATION_SETTINGS"));
    permissions.push(fullAccess("CC_ACCOUNT_PLAN"));
    permissions.push(
      screenPermission("USER_MANAGEMENT", {
        canCreate: isCallAdmin,
        canUpdate: true,
        canDelete: false,
      })
    );
    permissions.push(
      screenPermission("ROLE_MANAGEMENT", {
        canCreate: isCallAdmin,
        canUpdate: true,
        canDelete: isCallAdmin,
      })
    );
  }
  if (authorities.includes("ROLE_SUPER_ADMIN")) {
    permissions.push(fullAccess("ORG_MANAGEMENT", true));
  }

  return permissions;
};

const mapOrgFeatures = (securityMe) => {
  const callingEnabled = securityMe?.callingEnabled !== false;
  return {
    callingEnabled,
    webCallingEnabled: callingEnabled && securityMe?.webCallingEnabled !== false,
    desktopCallingEnabled: callingEnabled && securityMe?.desktopCallingEnabled !== false,
    aiCallingEnabled: securityMe?.aiCallingEnabled !== false,
  };
};

const mergePermissions = (derivedPermissions, apiPermissions) => {
  const byCode = new Map();
  derivedPermissions.forEach((perm) => byCode.set(perm.screenCode, perm));
  apiPermissions.forEach((perm) => {
    byCode.set(perm.screenCode, { ...byCode.get(perm.screenCode), ...perm });
  });
  return Array.from(byCode.values());
};

/**
 * Maps call-center `/api/security/me` to the shape stored in `userPermissions`
 * (compatible with `permissions.js` / `hasScreenAccess`).
 *
 * @param {{ role?: string, authorities?: string[], screenPermissions?: Array, callingEnabled?: boolean, aiCallingEnabled?: boolean }|null|undefined} securityMe
 * @returns {{ permissions: Array, fullAccess: boolean, orgFeatures: { callingEnabled: boolean, aiCallingEnabled: boolean } }}
 */
export const mapSecurityMeToPermissions = (securityMe) => {
  const authorities = Array.isArray(securityMe?.authorities) ? securityMe.authorities : [];
  const role = String(securityMe?.role || "").toUpperCase();
  const orgFeatures = mapOrgFeatures(securityMe);
  const isSuperAdmin =
    role === "SUPER_ADMIN" || authorities.includes("ROLE_SUPER_ADMIN");

  if (isSuperAdmin) {
    return { fullAccess: true, permissions: [], orgFeatures };
  }

  const derivedPermissions = deriveFromAuthorities(role, authorities);
  const apiScreenPermissions = mapScreenPermissionsFromApi(securityMe?.screenPermissions);
  if (apiScreenPermissions.length > 0) {
    return {
      fullAccess: false,
      permissions: mergePermissions(derivedPermissions, apiScreenPermissions),
      orgFeatures,
    };
  }

  return { fullAccess: false, permissions: derivedPermissions, orgFeatures };
};
